/**
 * useSubdomainLookup — form state machine for the "find your company"
 * email-based subdomain recovery flow.
 *
 * States: IDLE → LOADING → SUCCESS | ERROR. Caller injects `onSubmit(email)`
 * which returns a Promise (resolves on success, rejects on error). The
 * form never exposes subdomain in the response — by design, the server
 * emails the subdomain to the address.
 *
 * The state holds CATALOG KEYS; the hook returns their words in the viewer's
 * language. A refused send is worded by its CODE (`RATE_LIMIT_EXCEEDED`, the one
 * refusal the lookup route answers — it replies the same either way, so it
 * cannot be used to discover accounts), never by the failure's own message
 * (OWNER-RULINGS-WALK-BUILDS N138, `web/no-thrown-message-as-copy`). The two
 * validation sentences were English literals until then.
 *
 * @module @puffin-core/login-shell/hooks/useSubdomainLookup
 */

import { useReducer, useCallback, useEffect } from 'react';
import { isEmailAddress } from '@puffin-core/email-address';

import { useLoginTranslate } from './useLoginTranslate.js';

export const ModalState = Object.freeze({
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
});

const INITIAL_STATE = {
  email: '',
  modalState: ModalState.IDLE,
  validationError: '',
  apiError: '',
};

// Exported for direct unit testing of the pure state transitions (incl. the
// defensive `default` case that the hook never dispatches).
export function subdomainLookupReducer(state, action) {
  switch (action.type) {
    case 'SET_EMAIL':
      return { ...state, email: action.email, validationError: '', apiError: '' };
    case 'SUBMIT_START':
      return { ...state, modalState: ModalState.LOADING, apiError: '' };
    case 'SUBMIT_SUCCESS':
      return { ...state, modalState: ModalState.SUCCESS };
    case 'SUBMIT_ERROR':
      return { ...state, modalState: ModalState.ERROR, apiError: action.key };
    case 'SET_VALIDATION_ERROR':
      return { ...state, validationError: action.key };
    case 'RETRY':
      return { ...state, modalState: ModalState.IDLE, apiError: '' };
    case 'RESET':
      return INITIAL_STATE;
    default:
      return state;
  }
}

export function useSubdomainLookup({ isOpen, onSubmit }) {
  const [state, dispatch] = useReducer(subdomainLookupReducer, INITIAL_STATE);

  useEffect(() => {
    if (!isOpen) dispatch({ type: 'RESET' });
  }, [isOpen]);

  const handleEmailChange = useCallback((e) => {
    dispatch({ type: 'SET_EMAIL', email: e.target.value });
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      if (e?.preventDefault) e.preventDefault();
      const trimmed = state.email.trim();
      if (!trimmed) {
        dispatch({
          type: 'SET_VALIDATION_ERROR',
          key: 'modal.subdomainLookup.error.emailRequired',
        });
        return;
      }
      if (!isEmailAddress(trimmed)) {
        dispatch({ type: 'SET_VALIDATION_ERROR', key: 'modal.subdomainLookup.error.emailInvalid' });
        return;
      }
      dispatch({ type: 'SUBMIT_START' });
      try {
        if (onSubmit) await onSubmit(trimmed);
        dispatch({ type: 'SUBMIT_SUCCESS' });
      } catch (err) {
        dispatch({
          type: 'SUBMIT_ERROR',
          key:
            err?.errorCode === 'RATE_LIMIT_EXCEEDED'
              ? 'modal.subdomainLookup.error.rateLimited'
              : 'modal.subdomainLookup.error.failed',
        });
      }
    },
    [state.email, onSubmit],
  );

  const handleRetry = useCallback(() => {
    dispatch({ type: 'RETRY' });
  }, []);

  const translate = useLoginTranslate();
  return {
    email: state.email,
    modalState: state.modalState,
    validationError: state.validationError ? translate(state.validationError) : '',
    apiError: state.apiError ? translate(state.apiError) : '',
    isLoading: state.modalState === ModalState.LOADING,
    handleEmailChange,
    handleSubmit,
    handleRetry,
  };
}

export default useSubdomainLookup;
